/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { CircleWrapper, CircleInner } from '../PRIVATE_Meter';
import { getProgressClipPath } from '../utils/PRIVATE_meterUtils';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import {
  ProgressCircleStyles,
  ProgressCircleVariantOptions
} from './themes/ProgressCircleStyles.css';
import { ProgressCircleRedwoodTheme } from './themes/redwood/ProgressCircleTheme';
type ariaPropsType = ComponentProps<typeof CircleWrapper>['ariaProps'];
type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLDivElement>,
  'id' | 'class' | 'children'
>;

type Props = IntrinsicProps & {
  /**
   * Specifies the id of the progress circle.  If the progress circle is being used to describe the loading process of a particular region
   * of the page, then the aria-describedby attribute of the region must point to the id of the ProgressCircle
   */
  id?: IntrinsicProps['id'];
  /**
   * The value of the progress circle.  If the value is 'indeterminate', an indeterminate progress circle will be shown.
   */
  value?: 'indeterminate' | number;

  /**
   * The maximum value of the progress circle.
   */
  max?: number;
  /**
   * The size of the progress circle. The default value of this property is theme driven.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * A label to be used for accessibility purposes when value is set to 'indeterminate'. This will be used for the aria-valuetext of the DOM element.
   */
  'aria-valuetext'?: string;
};
/**
 * A progress circle allows the user to visualize the progression of an extended computer operation.
 */
export function ProgressCircle({ value, max, ...otherProps }: Props) {
  return value === 'indeterminate' ? (
    <IndeterminateProgressCircle {...otherProps}></IndeterminateProgressCircle>
  ) : (
    <DeterminateProgressCircle value={value} max={max} {...otherProps}></DeterminateProgressCircle>
  );
}

type IndeterminateProps = Omit<Props, 'value' | 'max'>;

const IndeterminateProgressCircle = ({ id, size, ...props }: IndeterminateProps) => {
  const {
    classes,
    styles: { indeterminate }
  } = useComponentTheme<ProgressCircleVariantOptions, ProgressCircleStyles>(
    ProgressCircleRedwoodTheme,
    {
      size,
      type: 'indeterminate'
    }
  );

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaProps: ariaPropsType = {
    'aria-valuetext': props['aria-valuetext'] || translations.progressIndeterminate(),
    role: 'progressbar'
  };
  return (
    <CircleWrapper id={id} ariaProps={ariaProps} class={classes}>
      <CircleInner class={indeterminate} />
    </CircleWrapper>
  );
};

type DeterminateProps = Omit<Props, 'aria-valuetext'> & {
  value?: number;
};

const DeterminateProgressCircle = ({ value = 0, max = 100, id, size }: DeterminateProps) => {
  const {
    classes,
    styles: { determinate, track, valueStyle }
  } = useComponentTheme<ProgressCircleVariantOptions, ProgressCircleStyles>(
    ProgressCircleRedwoodTheme,
    {
      size
    }
  );

  const percentage = Math.min(Math.max(0, value / max), 1);
  const clipPath = getProgressClipPath(percentage);

  const ariaProps: ariaPropsType = {};
  ariaProps['aria-valuemin'] = value != null ? 0 : undefined;
  ariaProps['aria-valuemax'] = value != null ? max : undefined;
  ariaProps['aria-valuenow'] = value != null ? value : undefined;
  ariaProps['role'] = 'progressbar';
  return (
    <CircleWrapper id={id} ariaProps={ariaProps} class={classes}>
      <CircleInner class={classNames([determinate, track])} />
      <CircleInner class={classNames([determinate, valueStyle])} clipPath={clipPath} />
    </CircleWrapper>
  );
};
